import { paths } from "../systems/userinput/paths";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { findAncestorWithComponent } from "../utils/scene-graph";
import isMobile from "../utils/is-mobile";
import nextTick from "../utils/next-tick";
import { ANIMATIONS_DICTIONARY } from "../utils/custom-utils";
import {
  ANIMATION_SELECTIONS,
  buildRuntimeAnimationClips,
  resolveAvatarCapabilities,
  retargetAnimationClip
} from "../utils/avatar-capabilities";

export const ANIMATIONS = {
  IDLE: "Idle",
  WALKING: "Walking",
  WALKING2: "walking",
  WALKING_BACKWARD: "WalkingBackwards",
  WALKING_LEFT: "LeftStrafeWalk",
  WALKING_RIGHT: "RightStrafeWalk",
  RUNNING: "Running",
  RUNNING_BACKWARD: "RunningBackward",
  RUNNING_LEFT: "LeftStrafe",
  RUNNING_RIGHT: "RightStrafe",
  FLYING: "Flying",
  SITLOW: "SitLowSeat",
  SITMEDIUM: "SitMediumSeat"
};

const SIT_MAPPER = {
  low: ANIMATIONS.SITLOW,
  medium: ANIMATIONS.SITMEDIUM
};

AFRAME.registerComponent("avatar-animation", {
  animations: null,
  clock: null,
  mixer: null,
  isMe: false,

  schema: {
    sitAnimation: { default: null },
    lockCamera: { default: false },
    front: { default: 0 },
    right: { default: 0 },
    lockedRotMatrix: { default: {} }
  },

  init() {
    this.networkedAvatar = findAncestorWithComponent(this.el, "networked-avatar");
    document.addEventListener("nipple-move", e => {
      if (this.isMe) {
        const { front, right } = e.detail;
        this.networkedAvatar?.setAttribute("networked-avatar", { front, right });
      }
    });

    this.animations = new Map();
    this.avatarObject = this.resolveAvatarObject();
    this.clock = new THREE.Clock();
    this.userinput = AFRAME.scenes[0].systems.userinput;
    this.isMe = this.el.closest("#avatar-rig") != null;
    this.ikController = findAncestorWithComponent(this.el, "ik-controller");
    this.setAnimations(this.el);
    this.avatarRig = this.el.closest("#avatar-rig");
    if (this.avatarRig) {
      this.avatarRig.addEventListener("landed", this.onLanded.bind(this));
    }
  },

  tick() {
    if (!this.mixer) return;
    this.mixer.update(this.clock.getDelta());

    if (this.isSit()) {
      if (this.ikController.components["ik-controller"].hasMaleAnim) {
        this.avatarObject.position.set(0, -0.25, -0.09);
      } else {
        this.avatarObject.position.set(0, -0.25, -0.12);
      }
      if (!this.attrValue.lockCamera) {
        this.ikController.components["ik-custom-controller"].lockAvatarRotation = false;
      } else {
        this.ikController.components["ik-custom-controller"].lockAvatarRotation = true;
        this.ikController.components["ik-controller"].cameraYRotation.setFromRotationMatrix(
          this.attrValue.lockedRotMatrix,
          "YXZ"
        );
      }

      const remoteSit =
        this.animations.get(ANIMATIONS.SITLOW)?.weight || this.animations.get(ANIMATIONS.SITMEDIUM)?.weight;

      if (!remoteSit) {
        this.sit();
      }
    } else {
      this.avatarObject.position.y = 0;
      this.ikController.components["ik-custom-controller"].lockAvatarRotation = false;
    }

    if (this.isMe && !isMobile()) {
      const position = this.userinput.get(paths.actions.characterAcceleration);
      if (position) {
        const [right, front] = position;
        this.el.setAttribute("avatar-animation", { front, right });
        this.networkedAvatar?.setAttribute("networked-avatar", { front, right });
      }
    }

    // Embedded animation clips can move the eye bones after IK runs. Reapply the
    // capability-scoped camera alignment after the mixer has evaluated this frame.
    this.ikController.components["ik-controller"].alignAvatarEyesToCamera?.();
  },

  update() {
    if (this.isSit()) {
      return this.sit();
    }

    if (this.isIdle()) {
      return this.idle();
    }

    this.walking();
  },

  async onLanded() {
    this.copiedRotationMatrix = new THREE.Matrix4();
    this.copiedRotationMatrix.extractRotation(this.ikController.components["ik-controller"].cameraForward);
    this.networkedAvatar?.setAttribute("networked-avatar", {
      lockCamera: true,
      lockedRotMatrix: this.copiedRotationMatrix
    });
  },

  isSit() {
    return !!this.attrValue.sitAnimation;
  },

  sit() {
    switch (this.attrValue.sitAnimation) {
      case ANIMATIONS.SITLOW:
        this.sitLow();
        break;
      case ANIMATIONS.SITMEDIUM:
        this.sitMedium();
        break;
      default:
        break;
    }
  },

  sitLow() {
    this.resetAll(ANIMATIONS.SITLOW);
    this.setEffectiveWeight(ANIMATIONS.SITLOW, 1);
  },

  sitMedium() {
    this.resetAll(ANIMATIONS.SITMEDIUM);
    this.setEffectiveWeight(ANIMATIONS.SITMEDIUM, 1);
  },

  mapSit(animation) {
    const sitAnimation = SIT_MAPPER[animation] ? SIT_MAPPER[animation] : "";
    this.networkedAvatar?.setAttribute("networked-avatar", { sitAnimation, lockCamera: false });
  },

  idle() {
    this.resetAll(ANIMATIONS.IDLE);
    this.setEffectiveWeight(ANIMATIONS.IDLE, 1);
  },

  resetAll(...ignore) {
    this.animations.forEach(animation => {
      if (ignore.includes(animation.getClip().name)) return;
      animation.setEffectiveWeight(0);
    });
  },

  isIdle() {
    return this.data.front === 0 && this.data.right === 0;
  },

  walking() {
    const forwardAnimations =
      this.capabilityReport?.recommendedSelection === ANIMATION_SELECTIONS.EMBEDDED
        ? [[ANIMATIONS.WALKING, this.data.front]]
        : [
            [ANIMATIONS.WALKING, this.data.front],
            [ANIMATIONS.WALKING2, this.data.front]
          ];

    const candidates = [
      ...forwardAnimations,
      [ANIMATIONS.WALKING_BACKWARD, -this.data.front],
      [ANIMATIONS.WALKING_RIGHT, -this.data.right],
      [ANIMATIONS.WALKING_LEFT, this.data.right]
    ];
    const groups = new Map();
    for (const [animationName, value] of candidates) {
      const source = this.animations.get(animationName)?.getClip()?.userData?.avatarRuntimeSource || animationName;
      const group = groups.get(source) || [];
      group.push([animationName, value]);
      groups.set(source, group);
    }
    for (const [source, group] of groups) {
      const active = group.find(([, value]) => value > 0);
      for (const [animationName, action] of this.animations) {
        if ((action.getClip()?.userData?.avatarRuntimeSource || animationName) !== source) continue;
        this.setEffectiveWeight(animationName, active?.[0] === animationName ? active[1] : 0);
      }
    }
  },

  async loadGLB(url) {
    const loader = new GLTFLoader();
    return new Promise((resolve, reject) => {
      loader.load(
        url,
        gltf2 => {
          resolve(gltf2);
        },
        undefined,
        reject
      );
    });
  },

  async waitForObject(scene, objectName, timeout = 600000, interval = 100) {
    const startTime = Date.now();

    const checkForObject = () => {
      const object = scene.object3D.getObjectByName(objectName);
      if (object) {
        return Promise.resolve(object);
      } else if (Date.now() - startTime > timeout) {
        return Promise.reject(new Error("Timeout waiting for object"));
      } else {
        return new Promise(resolve => setTimeout(resolve, interval)).then(checkForObject);
      }
    };

    return checkForObject();
  },

  resolveAvatarObject() {
    const modelRoot = this.el.closest?.(".model")?.object3D;
    const boundaryRoot = modelRoot || this.el.object3D.getObjectByName("model") || this.el.object3D;
    let avatarRoot = boundaryRoot;
    boundaryRoot.traverse(node => {
      if (
        avatarRoot === boundaryRoot &&
        node !== boundaryRoot &&
        (node.userData?.avatarCapabilities || (Array.isArray(node.animations) && node.animations.length > 0))
      ) {
        avatarRoot = node;
      }
    });
    const hips = resolveAvatarCapabilities(avatarRoot, avatarRoot.animations || []).humanoid.bones.hips;
    if (hips) return hips.parent || hips;
    return avatarRoot;
  },

  getAnimationSource() {
    const declaredSource = this.avatarObject?.userData?.avatarCapabilities?.animations?.source;
    if (declaredSource && declaredSource !== "embedded") return this.avatarObject;
    let source = this.avatarObject;
    while (source) {
      if (Array.isArray(source.animations) && source.animations.length > 0) return source;
      source = source.parent;
    }
    return this.avatarObject;
  },

  getAvatarMetadataSource(animationSource) {
    let source = animationSource;
    while (source) {
      if (source.userData?.avatarCapabilities || source.userData?.isMaleAnim || source.userData?.isFemaleAnim) {
        return source;
      }
      source = source.parent;
    }
    return animationSource;
  },

  async getLegacyAnimations(profile) {
    const scene = document.querySelector("a-scene");
    const mainName = profile === "male" ? ANIMATIONS_DICTIONARY["male_anim"] : ANIMATIONS_DICTIONARY["female_anim"];
    const idleName =
      profile === "male" ? ANIMATIONS_DICTIONARY["male_idle_anim"] : ANIMATIONS_DICTIONARY["female_idle_anim"];
    const main = await this.waitForObject(scene, mainName);
    const idle = await this.waitForObject(scene, idleName);
    if (!Array.isArray(main?.animations) || main.animations.length === 0) throw new Error("Missing legacy main clips.");
    if (!Array.isArray(idle?.animations) || idle.animations.length === 0) throw new Error("Missing legacy idle clips.");
    return { idle, main };
  },

  async setAnimations(el) {
    this.mixer?.stopAllAction();
    this.animations = this.animations || new Map();
    this.animations.clear();
    const animationSource = this.getAnimationSource();
    const metadataSource = this.getAvatarMetadataSource(animationSource);
    const report = resolveAvatarCapabilities(this.avatarObject, animationSource.animations || [], {
      sourceRoot: animationSource,
      metadataRoot: metadataSource
    });
    this.capabilityReport = report;

    if (report.recommendedSelection === ANIMATION_SELECTIONS.EMBEDDED && report.embedded.valid) {
      return this.setEmbeddedAnimations(report);
    }
    if (report.explicitSelection === ANIMATION_SELECTIONS.EMBEDDED) {
      return this.setNoAnimations(report);
    }
    if (report.explicitSelection === ANIMATION_SELECTIONS.NONE) {
      return this.setNoAnimations(report);
    }
    if (
      report.explicitSelection === ANIMATION_SELECTIONS.LEGACY_FEMALE ||
      report.explicitSelection === ANIMATION_SELECTIONS.LEGACY_MALE
    ) {
      const profile = report.explicitSelection === ANIMATION_SELECTIONS.LEGACY_MALE ? "male" : "female";
      try {
        const bundle = await this.getLegacyAnimations(profile);
        const legacyAnimations = [...bundle.idle.animations, ...bundle.main.animations];
        const compatible = resolveAvatarCapabilities(this.avatarObject, animationSource.animations || [], {
          legacyAnimations: { [profile]: legacyAnimations }
        }).legacy[`${profile}Compatible`];
        if (!compatible) return this.setNoAnimations(report);
        return this.setLegacyAnimations(el, profile, bundle);
      } catch {
        return this.setNoAnimations(report);
      }
    }
    if (report.hasDeclaredAnimationSource) return this.setNoAnimations(report);
    return this.setLegacyAnimations(el);
  },

  setNoAnimations(report) {
    this.avatarObject.visible = true;
    console.warn("Avatar animation selection started no animations.", {
      avatarUuid: this.avatarObject.uuid,
      selection: report.recommendedSelection
    });
  },

  setEmbeddedAnimations(report) {
    this.mixer = new THREE.AnimationMixer(this.avatarObject);
    const runtimeClips = buildRuntimeAnimationClips(report);

    this.runtimeClipSources = new Map();
    for (const [name, clip] of Object.entries(runtimeClips)) {
      const action = this.mixer.clipAction(clip);
      this.animations.set(name, action);
      this.runtimeClipSources.set(name, clip.userData?.avatarRuntimeSource);
      action.play();
      this.setEffectiveWeight(name, 0);
    }

    if (this.animations.size === 0) {
      console.warn("Embedded avatar capability report yielded no runtime clips.", {
        avatarUuid: this.avatarObject.uuid,
        report
      });
    } else {
      this.setEffectiveWeight(ANIMATIONS.IDLE, 1);
      this.mixer.update(0);
    }

    this.avatarObject.visible = true;
  },

  async setLegacyAnimations(el, requestedProfile, preloadedBundle) {
    try {
      const ikcontroller = findAncestorWithComponent(el, "ik-controller");
      if (ikcontroller.parentEl == null) return;
      if (ikcontroller.components["ik-controller"] && !ikcontroller.components["ik-controller"].fullBody) return;
      let animationGLTF;
      let idleGLTF;
      if (preloadedBundle) {
        ({ main: animationGLTF, idle: idleGLTF } = preloadedBundle);
      } else {
        const scene = document.querySelector("a-scene");
        const useMaleProfile =
          requestedProfile === "male" ||
          (requestedProfile === undefined && ikcontroller.components["ik-controller"].hasMaleAnim);
        const nameAnim = useMaleProfile ? ANIMATIONS_DICTIONARY["male_anim"] : ANIMATIONS_DICTIONARY["female_anim"];
        animationGLTF = await this.waitForObject(scene, nameAnim);
        const idleAnimName = useMaleProfile
          ? ANIMATIONS_DICTIONARY["male_idle_anim"]
          : ANIMATIONS_DICTIONARY["female_idle_anim"];
        try {
          idleGLTF = await this.waitForObject(scene, idleAnimName);
        } catch (e) {
          console.warn(`Idle animation object '${idleAnimName}' not found in scene.`);
        }
      }
      this.mixer = new THREE.AnimationMixer(this.avatarObject);
      await nextTick();

      const addRetargetedClips = (source, priority = 0) => {
        source?.animations?.forEach(sourceClip => {
          const clip = retargetAnimationClip(this.avatarObject, sourceClip);
          if (clip.tracks.length === 0) {
            console.warn(
              `Animation clip "${sourceClip.name}" has no valid tracks for avatar "${this.avatarObject.uuid}" and will be skipped.`
            );
            return;
          }
          const action = this.mixer.clipAction(clip);
          action.priority = priority;
          this.animations.set(clip.name, action);
          action.play();
          this.setEffectiveWeight(clip.name, 0);
        });
      };

      addRetargetedClips(idleGLTF, 10);
      addRetargetedClips(animationGLTF);

      // Ensure Idle is still set if available and valid
      if (this.animations.has(ANIMATIONS.IDLE)) {
        this.setEffectiveWeight(ANIMATIONS.IDLE, 1);
      } else if (this.animations.size > 0) {
        const firstAnimationName = this.animations.keys().next().value;
        console.warn(
          `Default IDLE animation not found or filtered out. Setting first available animation "${firstAnimationName}" as idle.`
        );
        this.setEffectiveWeight(firstAnimationName, 1);
      } else {
        console.warn("No valid animations found for this avatar after filtering.");
      }
    } catch (error) {
      console.error("Error in setAnimations:", error);
      this.avatarObject.visible = true; // Original fallback
    }
  },

  setEffectiveWeight(animationName, weight) {
    const action = this.animations.get(animationName);
    if (!action) return;
    action.setEffectiveWeight(weight);
    // If this is an idle animation and weight > 0, make sure it's playing
    if (weight > 0 && action.priority === 10 && !action.isRunning()) {
      action.reset();
      action.play();
    }
  }
});
